import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, ArrowDownCircle, ArrowUpCircle } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";

export default function Stock() {
  const { apiCall } = useAuth();
  const [movements, setMovements] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ product_id: "", movement_type: "entrada", quantity: "", notes: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [mRes, pRes] = await Promise.all([
        apiCall("get", "/stock"),
        apiCall("get", "/products"),
      ]);
      setMovements(mRes.data);
      setProducts(pRes.data);
    } catch {
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiCall("post", "/stock", {
        ...form,
        quantity: parseInt(form.quantity),
      });
      toast.success("Movimentação registrada");
      setOpen(false);
      setForm({ product_id: "", movement_type: "entrada", quantity: "", notes: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao registrar");
    }
  };

  const formatDate = (date) =>
    new Date(date).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });

  return (
    <div className="space-y-8" data-testid="stock-page">
      <PageHeader overline="Operações" title="Estoque" subtitle="Controle entradas e saídas">
        <Button onClick={() => setOpen(true)} data-testid="add-stock-button" disabled={products.length === 0}>
          <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />
          Nova Movimentação
        </Button>
      </PageHeader>

      {products.length === 0 && (
        <Card className="border border-border bg-card p-6 shadow-soft rounded-md">
          <p className="text-sm text-muted-foreground">Cadastre produtos primeiro para movimentar o estoque.</p>
        </Card>
      )}

      <Card className="border border-border bg-card shadow-soft rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/30 hover:bg-secondary/30">
              <TableHead className="text-xs uppercase tracking-widest font-medium">Data</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Produto</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Tipo</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Qtd</TableHead>
              <TableHead className="text-xs uppercase tracking-widest font-medium">Observações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
            ) : movements.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-12 text-muted-foreground">Nenhuma movimentação registrada.</TableCell></TableRow>
            ) : (
              movements.map((m) => (
                <TableRow key={m.movement_id} data-testid={`stock-row-${m.movement_id}`}>
                  <TableCell className="text-sm text-muted-foreground">{formatDate(m.created_at)}</TableCell>
                  <TableCell className="font-medium">{m.product_name}</TableCell>
                  <TableCell>
                    {m.movement_type === "entrada" ? (
                      <div className="flex items-center gap-2 text-sm" style={{ color: "#166534" }}>
                        <ArrowDownCircle className="h-4 w-4" strokeWidth={1.5} />
                        Entrada
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-sm text-destructive">
                        <ArrowUpCircle className="h-4 w-4" strokeWidth={1.5} />
                        Saída
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="font-medium">{m.quantity}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{m.notes || "—"}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-light tracking-tight text-2xl">Nova Movimentação</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div>
              <Label className="text-xs uppercase tracking-widest">Produto</Label>
              <Select value={form.product_id} onValueChange={(v) => setForm({ ...form, product_id: v })} required>
                <SelectTrigger data-testid="stock-product-select"><SelectValue placeholder="Selecione um produto" /></SelectTrigger>
                <SelectContent>
                  {products.map((p) => (
                    <SelectItem key={p.product_id} value={p.product_id} data-testid={`stock-product-option-${p.product_id}`}>
                      {p.name} (Estoque: {p.stock_quantity})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Tipo</Label>
              <Select value={form.movement_type} onValueChange={(v) => setForm({ ...form, movement_type: v })}>
                <SelectTrigger data-testid="stock-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="entrada" data-testid="stock-type-entrada">Entrada</SelectItem>
                  <SelectItem value="saida" data-testid="stock-type-saida">Saída</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Quantidade</Label>
              <Input type="number" min="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} required data-testid="stock-quantity-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest">Observações</Label>
              <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="stock-notes-input" />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
              <Button type="submit" data-testid="stock-save-button">Registrar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
